import React from "react";
import useSWR from "swr";
import { Link } from "react-router-dom";
import { Bell, Volume2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import api from "@/lib/api";
import { playPaySound } from "@/lib/paySound";
import { useAuth } from "@/context/AuthContext";

const fetcher = (url) => api.get(url).then((r) => r.data);

const NotificationBell = ({ compact = false }) => {
  const { user } = useAuth();
  const inboxPath = user?.role === "driver" ? "/driver/notifications" : "/admin/notifications";
  const { data = [], mutate } = useSWR("/notifications", fetcher, { refreshInterval: 10_000, revalidateOnFocus: true });
  const { data: settings = { pay_sound: "myportal_signature", volume: 0.8 } } = useSWR("/notifications/settings", fetcher);
  const unread = data.filter((n) => !n.is_read);

  React.useEffect(() => {
    const latest = unread[0];
    if (!latest) return;
    if (latest.sound === "myportal_pay" && localStorage.getItem("myportal-sound-enabled") === "true") {
      const key = `myportal-sounded-${latest.id}`;
      if (!localStorage.getItem(key) && playPaySound(settings.pay_sound, settings.volume)) localStorage.setItem(key, "1");
    }
    const notificationKey = `myportal-notified-${latest.id}`;
    if ("Notification" in window && Notification.permission === "granted" && !localStorage.getItem(notificationKey)) {
      new Notification(latest.title, { body: latest.body, tag: latest.id });
      localStorage.setItem(notificationKey, "1");
    }
  }, [unread, settings.pay_sound, settings.volume]);

  const mark = async (n) => {
    if (!n.is_read) await api.post(`/notifications/${n.id}/read`);
    mutate();
  };
  const enableSound = async () => {
    localStorage.setItem("myportal-sound-enabled", "true");
    playPaySound(settings.pay_sound, settings.volume);
    if ("Notification" in window && Notification.permission === "default") {
      await Notification.requestPermission();
    }
  };

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="ghost" size="sm" className="relative min-w-11 min-h-11 pointer-events-auto" aria-label={`${unread.length} unread notifications`}>
          <Bell className="w-4 h-4" />
          {unread.length > 0 && <span className="absolute -top-1 -right-1 min-w-[16px] h-4 px-1 rounded-full bg-brand-red text-white text-[9px] grid place-items-center">{unread.length > 99 ? "99+" : unread.length}</span>}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className={`${compact ? "w-72" : "w-80"} p-0`}>
        <div className="p-3 border-b border-brand-border flex items-center justify-between">
          <div><div className="text-[10px] uppercase tracking-wider text-brand-text-muted">Notifications</div><div className="font-heading font-bold">{unread.length} unread</div></div>
          <Button size="sm" variant="outline" onClick={enableSound} title="Enable the MY PORTAL pay sound"><Volume2 className="w-3.5 h-3.5" /></Button>
        </div>
        <div className="max-h-80 overflow-y-auto divide-y divide-brand-border">
          {data.slice(0, 8).map((n) => (
            <Link key={n.id} to={n.link || inboxPath} onClick={() => mark(n)} className={`block p-3 text-xs hover:bg-brand-surface ${n.is_read ? "opacity-70" : "bg-red-50/40"}`}>
              <div className="font-semibold">{n.title}</div><div className="mt-1 text-brand-text-muted line-clamp-2">{n.body}</div>
            </Link>
          ))}
          {!data.length && <div className="p-6 text-center text-xs text-brand-text-muted">No notifications yet.</div>}
        </div>
        <Link to={inboxPath} className="block p-2.5 text-center text-xs text-brand-navy border-t border-brand-border">Open notification chat →</Link>
      </PopoverContent>
    </Popover>
  );
};

export default NotificationBell;
